import type { CheckoutSessionDTO, CheckoutSubMode, QRPaymentResponseDTO } from './checkoutApi.js';

/** Checkout sessions that must not start another payment (reuse requires a new session). */
export function isTerminalCheckoutSessionStatus(
  status: CheckoutSessionDTO['status'] | null | undefined
): boolean {
  return status === 'COMPLETED' || status === 'EXPIRED' || status === 'CANCELLED';
}

/**
 * G1 — after optimistic COMPLETED (or EXPIRED/CANCELLED), a lagging GET can still
 * return AWAITING_PAYMENT / OPEN. Prefer the cached terminal status so refetch
 * cannot regress the session cache (keeps remount terminal shop recovery alive).
 */
export function preferTerminalCheckoutSessionStatus(
  cached: CheckoutSessionDTO | undefined,
  fetched: CheckoutSessionDTO,
): CheckoutSessionDTO {
  if (
    cached !== undefined &&
    isTerminalCheckoutSessionStatus(cached.status) &&
    !isTerminalCheckoutSessionStatus(fetched.status)
  ) {
    return { ...fetched, status: cached.status };
  }
  return fetched;
}

/** Mobile PWA bank QR is only offered for prepay-collect-later (enterprise QR policy). */
export function isMobileBankQrAllowedForCheckoutMode(
  mode: CheckoutSubMode | null | undefined
): boolean {
  return mode === 'PREPAY_COLLECT_LATER';
}

/** Journey B-mobile: gateway only — no bank QR on PAY_NOW_SELF_SERVICE. */
export function isMobileGatewayOnlyForCheckoutMode(
  mode: CheckoutSubMode | null | undefined
): boolean {
  return mode === 'PAY_NOW_SELF_SERVICE';
}

export function resolveQrVisibleFromStartQrResponse(qr: Pick<
  QRPaymentResponseDTO,
  'qrVisible' | 'qrAccess' | 'paymentId'
>): boolean {
  if (qr.qrVisible === false || qr.qrAccess === 'denied') {
    return false;
  }
  if (qr.qrVisible === true || qr.qrAccess === 'allowed') {
    return true;
  }
  // Resume stub / legacy start-qr: payment exists and was not explicitly denied.
  // QrPaymentView loads PNG via paymentId / checkout-session proxy.
  return (qr.paymentId?.trim().length ?? 0) > 0;
}

/** Re-open QR UI for an in-flight checkout session after reload or collect lock. */
export function buildQrResumeFromCheckoutSession(
  session: Pick<
    CheckoutSessionDTO,
    'status' | 'paymentId' | 'amountMinor' | 'currency'
  >
): QRPaymentResponseDTO | null {
  const paymentId = session.paymentId?.trim() ?? '';
  if (session.status !== 'AWAITING_PAYMENT' || paymentId.length === 0) {
    return null;
  }
  return {
    paymentId,
    amountMinor: session.amountMinor,
    currency: session.currency,
    variableSymbol: '',
    // Unpaid reuse: PNG proxy can load without SPAYD. Never omit these —
    // missing qrVisible/qrAccess was treated as deny and hid the QR after Back.
    qrVisible: true,
    qrAccess: 'allowed',
  };
}

/**
 * Live QR step source. Explicit `qr` always wins.
 * When `suppressAutoResumeQr` (back from mid-payment QR), do not promote
 * `pendingQr` / session resume into the QR step — payment step keeps
 * `pendingQr` for the resume banner only (G6 / MOB-05).
 */
export function resolveActiveQrPayment(input: {
  readonly qr: QRPaymentResponseDTO | null;
  readonly pendingQr: QRPaymentResponseDTO | null;
  readonly resumedQrFromSession: QRPaymentResponseDTO | null;
  readonly suppressAutoResumeQr: boolean;
}): QRPaymentResponseDTO | null {
  if (input.qr !== null) {
    return input.qr;
  }
  if (input.suppressAutoResumeQr) {
    return null;
  }
  return input.pendingQr ?? input.resumedQrFromSession;
}
